import { useCallback, useEffect, useRef, useState } from "react";
import { BLOCK_REASONS, type BlockReasonCode, type StoredBlockReasonCode } from "../domain/account-block-v2";
import { api } from "./api";
import { EmptyMark, LoadingState } from "./ParticipantShell";

export interface OwnBlock {
  blockedId: string;
  displayName: string | null;
  reasonCode: StoredBlockReasonCode;
  note: string | null;
}
export interface BlockInput { reasonCode: BlockReasonCode; note?: string }
const errorText = (error: unknown) => error instanceof Error ? error.message : "请求未能完成";

/** Native modal focus trap; each opening is a fresh, unsaved choice. */
export function BlockReasonSheet({ initial, onSave, onClose, onReport }: {
  initial?: OwnBlock;
  onSave: (input: BlockInput) => Promise<void>;
  onClose: () => void;
  onReport?: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [code, setCode] = useState<BlockReasonCode | "">("");
  const [note, setNote] = useState(initial?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { dialog.current?.showModal(); }, []);
  const close = () => { if (!busy) { dialog.current?.close(); onClose(); } };
  const save = async () => {
    if (!code || busy) return;
    setBusy(true); setError("");
    try {
      await onSave({ reasonCode: code, ...(code === "other" && note !== "" ? { note } : {}) });
      dialog.current?.close(); onClose();
    } catch (caught) { setError(errorText(caught)); } finally { setBusy(false); }
  };
  return <dialog ref={dialog} className="account-dialog r4-safety-dialog r4-block-sheet" aria-labelledby="block-title"
    onCancel={(event) => { event.preventDefault(); close(); }}>
    <h2 id="block-title" tabIndex={-1}>{initial ? "更改屏蔽原因" : "屏蔽这个人"}</h2>
    {!initial && <p className="dialog-copy">屏蔽后，你们在 Perhaps 的任何地方都不会再联系到彼此。对方不会收到通知，也不会知道原因。</p>}
    <fieldset className="r4-report-field" disabled={busy}><legend>可以告诉我们原因吗？原因只有你自己能看到，我们不会告诉任何人。</legend>
      {BLOCK_REASONS.map((reason) => <label key={reason.code} className="r4-option">
        <input type="radio" name="block-reason" value={reason.code} checked={code === reason.code} onChange={() => { setCode(reason.code); if (reason.code !== "other") setNote(""); }} />
        <span>{reason.label}</span>
      </label>)}
    </fieldset>
    {code === "other" && <label className="dialog-field"><span>补充说明（可选，最多 200 个字）</span>
      <textarea rows={3} value={note} disabled={busy} onChange={(event) => setNote(event.target.value)} />
    </label>}
    {onReport && <p className="dialog-copy">如果遇到了让你不安的事，也可以<span className="r4-block-report-link"><button className="text-button" disabled={busy} onClick={() => { close(); onReport(); }}>举报</button>。</span></p>}
    {error && <p className="error-message" role="alert">{error}</p>}
    {Array.from(note).length > 200 && <p className="error-message" role="alert">补充说明最多 200 个字</p>}
    <div className="dialog-actions">
      <button className="button" disabled={busy} onClick={close}>取消</button>
      <button className="button" disabled={busy || !code || Array.from(note).length > 200} aria-busy={busy || undefined} onClick={() => void save()}>{busy ? "Saving" : initial ? "保存" : "屏蔽"}</button>
    </div>
  </dialog>;
}

export function BlockedPeople({ onChanged }: { onChanged: () => void }) {
  const [items, setItems] = useState<OwnBlock[] | null>(null);
  const [edit, setEdit] = useState<OwnBlock | null>(null);
  const [removing, setRemoving] = useState<OwnBlock | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const refresh = useCallback(async () => {
    try { setItems((await api<{ blocks: OwnBlock[] }>("/api/v1/pairing/blocks")).blocks); setError(""); }
    catch (caught) { setError(errorText(caught)); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { if (removing) dialog.current?.showModal(); }, [removing]);
  const closeRemove = () => { if (!busy) { dialog.current?.close(); setRemoving(null); } };
  const unblock = async () => {
    if (!removing || busy) return;
    setBusy(true); setError("");
    try {
      await api(`/api/v1/pairing/blocks/${removing.blockedId}`, { method: "DELETE" });
      setItems((current) => current?.filter((item) => item.blockedId !== removing.blockedId) ?? null);
      dialog.current?.close(); setRemoving(null); onChanged();
    } catch (caught) { setError(errorText(caught)); } finally { setBusy(false); }
  };
  return <section className="r4-blocked-people">
    {items === null ? error ? <section className="pairing-empty"><h2>暂时无法读取</h2><button className="button" onClick={() => void refresh()}>Try again</button></section> : <LoadingState />
      : items.length === 0 ? <section className="pairing-empty"><EmptyMark /><h2>No one blocked</h2></section>
        : items.map((item) => <article className="r4-group r4-blocked-person" key={item.blockedId}>
          <h2>{item.displayName ?? "已屏蔽的参与者"}</h2>
          <p>{BLOCK_REASONS.find((reason) => reason.code === item.reasonCode)?.label ?? "未记录原因"}</p>
          {item.note !== null && <p className="r4-block-note">{item.note}</p>}
          <div className="r4-choice-actions">
            <button className="button" onClick={() => setEdit(item)}>更改原因</button>
            <button className="button" onClick={() => { setError(""); setRemoving(item); }}>解除屏蔽</button>
          </div>
        </article>)}
    {error && !removing && <p className="error-message" role="alert">{error}</p>}
    {edit && <BlockReasonSheet initial={edit} onClose={() => setEdit(null)} onSave={async (input) => {
      await api(`/api/v1/pairing/blocks/${edit.blockedId}`, { method: "PUT", body: JSON.stringify(input) });
      setItems((current) => current?.map((item) => item.blockedId === edit.blockedId ? { ...item, ...input, note: input.note ?? null } : item) ?? null);
    }} />}
    <dialog ref={dialog} className="account-dialog" aria-labelledby="unblock-title" onCancel={(event) => { event.preventDefault(); closeRemove(); }}>
      <h2 id="unblock-title" tabIndex={-1}>解除屏蔽？</h2>
      <p className="dialog-copy">解除后，你们可以重新发送消息和读取彼此分享的联系方式。对方不会收到通知。</p>
      {error && <p className="error-message" role="alert">{error}</p>}
      <div className="dialog-actions"><button className="button" disabled={busy} onClick={closeRemove}>取消</button><button className="button" disabled={busy} onClick={() => void unblock()}>{busy ? "Processing" : "解除屏蔽"}</button></div>
    </dialog>
  </section>;
}
